export type Severity = 'critical' | 'watch' | 'fyi';

export type Agent = {
  id: string;
  name: string;
  short: string;
  role: string;
  color: string;
  icon: string;
};

export type RichReport = {
  theme: 'commerce' | 'inventory' | 'markettime' | 'finance';
  title: string;
  metrics: { label: string; value: string }[];
  feature?: { label: string; value: string; sub?: string };
  lists: { title: string; tone?: 'neutral' | 'rose' | 'sage'; items: { label: string; value?: string; note?: string }[] }[];
  note?: string;
};

export type PaymentPendingOrder = {
  orderNumber: string;
  customer: string;
  total: number;
  createdAt: string;
  financialStatus: string;
  shipDate?: string;
};

export type PaymentPendingSummary = {
  date: string;
  todayCount: number;
  todayTotal: number;
  totalCount: number;
  totalValue: number;
  olderCount: number;
  olderTotal: number;
  orders: PaymentPendingOrder[];
};

export type InventoryItem = {
  sku: string;
  product: string;
  variant: string;
  shopifyOnHand: number;
  sellableOnline: number;
  price?: number;
  unitsSold30d?: number;
  daysRemaining?: number | null;
  fishbowlMatched: boolean;
  fishbowlOnHand?: number | null;
  fishbowlIncoming?: number | null;
  fishbowlDemand?: number | null;
  fishbowlAction?: string;
  priority: 'urgent' | 'watch' | 'healthy';
  reason?: string;
};

export type InventorySnapshot = {
  laneId: 'shopify-retail' | 'shopify-wholesale';
  sourceAt: string;
  fishbowlAt?: string;
  summary: {
    itemCount: number;
    totalShopifyOnHand: number;
    outOfStock: number;
    lowStock: number;
    fishbowlMatched: number;
    fishbowlOnHand: number;
  };
  urgent: InventoryItem[];
  items: InventoryItem[];
};

export type Report = {
  id: string;
  agentId: string;
  time: string;
  headline: string;
  severity: Severity;
  attention: string;
  action: string;
  hero: { value: string; label: string; sub: string; paymentPending?: PaymentPendingSummary };
  inventory?: InventorySnapshot;
  kpis: { label: string; value: string; delta?: number }[];
  series?: number[];
  source: string;
  sourceAt?: string;
  sourceNote?: string;
  confidence: 'high' | 'medium' | 'low';
  summary: string;
  rich?: RichReport;
};

export const agents: Agent[] = [
  { id: 'owner', name: 'Thomas Blonde AI', short: 'TB', role: 'Owner brief', color: '#B88B53', icon: 'sparkles-outline' },
  { id: 'wholesale', name: 'Wholesale', short: 'WS', role: 'Shopify wholesale', color: '#5FAFB0', icon: 'cube-outline' },
  { id: 'retail', name: 'Retail', short: 'RT', role: 'Shopify retail', color: '#DD8265', icon: 'bag-handle-outline' },
  { id: 'fishbowl', name: 'Fishbowl', short: 'FB', role: 'Inventory + production', color: '#8AA275', icon: 'layers-outline' },
  { id: 'markettime', name: 'MarketTime', short: 'MT', role: 'Reps + wholesale growth', color: '#765A71', icon: 'people-circle-outline' },
  { id: 'quickbooks', name: 'QuickBooks', short: 'QB', role: 'Finance + cash', color: '#9A765D', icon: 'wallet-outline' },
];

export const agentOrder = ['owner', 'wholesale', 'retail', 'fishbowl', 'markettime', 'quickbooks'] as const;

export function sortReportsByAgentOrder<T extends Pick<Report, 'agentId'>>(items: T[]): T[] {
  const rank = new Map<string, number>(agentOrder.map((id, index) => [id, index]));
  return [...items].sort((a, b) => (rank.get(a.agentId) ?? agentOrder.length) - (rank.get(b.agentId) ?? agentOrder.length));
}

export const reports: Report[] = [
  {
    id: 'owner-1', agentId: 'owner', time: '8:02 AM', severity: 'watch',
    headline: 'Sales are healthy, but the Holiday candle needs a production decision',
    attention: 'Holiday candle inventory falls inside the production lead-time window on Tuesday.',
    action: 'Approve a 600-unit production run before noon.',
    hero: { value: '$31.8k', label: 'combined sales', sub: '+11.4% versus last week' },
    kpis: [{ label: 'Retail', value: '$18.4k', delta: 14.2 }, { label: 'Wholesale', value: '$13.4k', delta: 7.8 }, { label: 'Cash', value: '$184k' }],
    series: [24.1, 26.8, 25.4, 28.9, 27.5, 30.2, 31.8], source: 'Shopify + Fishbowl + QuickBooks', confidence: 'high',
    summary: 'Revenue is pacing above last week across both channels. The only owner-level decision today is replenishing Holiday before available stock crosses inside its 18-day production lead time.'
  },
  {
    id: 'wholesale-1', agentId: 'wholesale', time: '7:15 AM', severity: 'watch',
    headline: 'Wholesale grew 8%, while two large draft orders remain unconfirmed',
    attention: 'Two draft orders worth $6,840 have been open for more than five days.',
    action: 'Follow up with the Dallas and Nashville accounts this morning.',
    hero: { value: '$13,406', label: 'wholesale revenue', sub: '18 orders across 14 accounts' },
    kpis: [{ label: 'Orders', value: '18', delta: 5.9 }, { label: 'AOV', value: '$745', delta: 1.8 }, { label: 'Open drafts', value: '$6.8k' }],
    series: [9.8, 11.1, 10.4, 12.2, 11.7, 12.6, 13.4], source: 'Shopify B2B orders', confidence: 'high',
    summary: 'Booked revenue is moving in the right direction. Converting the two aging drafts would materially improve this week’s wholesale total.'
  },
  {
    id: 'retail-1', agentId: 'retail', time: '7:00 AM', severity: 'fyi',
    headline: 'Retail revenue rose 14% with stronger conversion and stable order value',
    attention: '', action: 'Keep the current merchandising mix in place today.',
    hero: { value: '$18,412', label: 'retail revenue', sub: '142 orders at $130 average' },
    kpis: [{ label: 'Orders', value: '142', delta: 12.7 }, { label: 'AOV', value: '$130', delta: 1.4 }, { label: 'Conversion', value: '2.8%', delta: 8.1 }],
    series: [12.8, 14.1, 13.4, 16.2, 15.8, 17.1, 18.4], source: 'Shopify retail orders', confidence: 'high',
    summary: 'Traffic and conversion both improved. No unusual discounting or refund activity appeared in yesterday’s retail orders.'
  },
  {
    id: 'fishbowl-1', agentId: 'fishbowl', time: '7:30 AM', severity: 'critical',
    headline: 'Holiday candle will cross its safety stock before replenishment can land',
    attention: 'At the current sell-through rate, Holiday has 15 days of cover against an 18-day lead time.',
    action: 'Release a 600-unit work order today.',
    hero: { value: '15 days', label: 'Holiday stock cover', sub: '3 days inside production lead time' },
    kpis: [{ label: 'Available', value: '418' }, { label: 'Daily demand', value: '27.6' }, { label: 'Suggested run', value: '600' }],
    series: [24, 23, 21, 20, 18, 17, 15], source: 'Fishbowl inventory availability', confidence: 'medium',
    summary: 'Holiday is the only SKU currently inside its lead-time risk window. Demand includes open wholesale allocations; three unconfirmed drafts make the forecast moderately uncertain.'
  },
  {
    id: 'markettime-1', agentId: 'markettime', time: 'Latest', severity: 'fyi',
    headline: 'MarketTime is ready to publish rep and wholesale performance',
    attention: '', action: 'Open the private live dashboard for the verified MarketTime report.',
    hero: { value: 'Connected', label: 'MarketTime reporting', sub: 'Live figures load only on the private dashboard' },
    kpis: [{ label: 'Reps', value: 'Live' }, { label: 'Orders', value: 'Live' }, { label: 'Accounts', value: 'Live' }],
    source: 'MarketTime private connector', confidence: 'medium',
    summary: 'The MarketTime lane is connected. Verified company figures are kept out of the public demo bundle and load only from the private live snapshot.'
  },
  {
    id: 'quickbooks-1', agentId: 'quickbooks', time: '7:45 AM', severity: 'fyi',
    headline: 'Cash remains comfortable after payroll with receivables trending down',
    attention: '', action: 'Review the three uncategorized bank transactions when convenient.',
    hero: { value: '$184,260', label: 'available cash', sub: '8.7 weeks of operating cover' },
    kpis: [{ label: 'A/R', value: '$42.1k', delta: -8.4 }, { label: 'A/P', value: '$31.8k' }, { label: 'Uncategorized', value: '3' }],
    series: [171, 176, 174, 181, 179, 191, 184], source: 'QuickBooks balance sheet + banking', confidence: 'high',
    summary: 'Cash coverage remains above the internal eight-week target. Receivables improved and there are no badly overdue invoices requiring escalation today.'
  },
];
